import { useCopyToClipboard, useShareOrCopyHandler } from "./use-share-or-copy";
import { toValue, type MaybeRefOrGetter } from "vue";
import isMobile from "is-mobile";
import { useTrackingStore } from "@/stores/tracking.store";
import { TrackEventEnum } from "@/services/tracking/tracking-types";
import { getEventCategoryFromExperienceType } from "@/utils/referrals";
import {
  useGetReferralLink,
  type OpenGraphObjectPayload,
  type ReferralObjectPayload,
} from "./use-get-referral-link";

export function useShareReferral({
  openGraphObject,
  referralObject,
}: {
  openGraphObject: MaybeRefOrGetter<OpenGraphObjectPayload>;
  referralObject: MaybeRefOrGetter<ReferralObjectPayload>;
}) {
  const trackingService = useTrackingStore();

  const shareOrCopy = useShareOrCopyHandler();
  const copyToClipboard = useCopyToClipboard();

  const getReferralLink = useGetReferralLink({
    openGraphObject,
    referralObject,
  });

  async function shareReferralLink(): Promise<void> {
    const value = toValue(referralObject);
    const openGraphPayload = toValue(openGraphObject);

    trackingService.track({
      event: TrackEventEnum.GenerateReferralLink,
      category: getEventCategoryFromExperienceType(
        value.payload?.experience_type,
      ),
      metadata: {
        experienceId: value.payload?.experience_id,
        experienceType: value.payload?.experience_type,
        experienceTitle: openGraphPayload.title ?? undefined,
      },
    });

    if (isMobile()) {
      const link = await getReferralLink();
      if (!link) return;

      return void shareOrCopy(
        {
          url: link,
          // Intentionally not passing title and text to the share function
          // so on mobile it just shares the link without any additional text.
          title: undefined,
          text: undefined,
        },
        link,
      );
    } else {
      const linkPromise = getReferralLink();

      // Here we're trying to copy to the clipboard as a result of an API call
      // instead of in the context of a user interaction. This is not allowed.
      // so we have to workaround it.
      // From https://stackoverflow.com/a/77517883
      // and https://developer.apple.com/forums/thread/691873?answerId=693193022#693193022
      return void copyToClipboard(
        new ClipboardItem({
          "text/plain": new Promise<Blob>((resolve, reject) => {
            linkPromise
              .then((link) => {
                if (link) {
                  resolve(new Blob([link], { type: "text/plain" }));
                } else {
                  reject(new Error("Failed to create referral link"));
                }
              })
              .catch(reject);
          }),
        }),
      );
    }
  }

  return shareReferralLink;
}
